/**
 * Compact QR encoding/decoding for army lists.
 *
 * Uses short field names + browser-native deflate-raw compression + base64 to
 * keep the payload well under the ~2953-byte QR code limit even for large lists.
 *
 * Format: "BSv1:<base64-deflated-json>"
 * Legacy fallback: raw JSON (generated by older app versions).
 */

import type { ArmyList, ArmyEntry } from '../types/army';

const PREFIX = 'BSv1:';

// ── Compact schema ────────────────────────────────────────────────────────────

interface CompactEntry {
  u: string;                          // unitId
  q: number;                          // quantity
  b: number;                          // bitmask: champion=1, standard=2, musician=4
  o: string[];                        // selectedOptions
  vi?: string;                        // selectedVirtueId
  i?: string[];                       // selectedMagicItemIds
  oq?: Record<string, number>;        // optionQuantities
  mt?: string;                        // selectedMountId
  cn?: string;                        // customName
  l?: string;                         // selectedLoreKey
  ug?: string[];                      // selectedUpgradeIds
}

interface Compact {
  v: 1;
  n: string;                          // name
  f: string;                          // factionId
  c: string;                          // compositionId
  s?: string;                         // subOrderId
  m: string[];                        // matchedPlayFormats
  p: number;                          // pointsLimit
  e: CompactEntry[];
}

// ── Pack / unpack ─────────────────────────────────────────────────────────────

function packEntry(entry: ArmyEntry): CompactEntry {
  const b =
    (entry.includeChampion ? 1 : 0) |
    (entry.includeStandard ? 2 : 0) |
    (entry.includeMusician ? 4 : 0);
  const ce: CompactEntry = { u: entry.unitId, q: entry.quantity, b, o: entry.selectedOptions };
  if (entry.selectedVirtueId) ce.vi = entry.selectedVirtueId;
  if (entry.selectedMagicItemIds.length) ce.i = entry.selectedMagicItemIds;
  if (entry.optionQuantities && Object.keys(entry.optionQuantities).length) ce.oq = entry.optionQuantities;
  if (entry.selectedMountId) ce.mt = entry.selectedMountId;
  if (entry.customName) ce.cn = entry.customName;
  if (entry.selectedLoreKey) ce.l = entry.selectedLoreKey;
  if (entry.selectedUpgradeIds?.length) ce.ug = entry.selectedUpgradeIds;
  return ce;
}

function unpackEntry(ce: CompactEntry): ArmyEntry {
  return {
    id: crypto.randomUUID(),
    unitId: ce.u,
    quantity: ce.q,
    includeChampion: !!(ce.b & 1),
    includeStandard: !!(ce.b & 2),
    includeMusician: !!(ce.b & 4),
    selectedOptions: ce.o,
    selectedVirtueId: ce.vi ?? null,
    selectedMagicItemIds: ce.i ?? [],
    optionQuantities: ce.oq,
    selectedMountId: ce.mt ?? null,
    customName: ce.cn,
    selectedLoreKey: ce.l,
    selectedUpgradeIds: ce.ug,
  };
}

// ── Compress / decompress (browser-native, no external dep) ───────────────────

async function compress(text: string): Promise<string> {
  const input = new TextEncoder().encode(text);
  const cs = new CompressionStream('deflate-raw');
  const writer = cs.writable.getWriter();
  writer.write(input);
  writer.close();

  const chunks: Uint8Array[] = [];
  const reader = cs.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) { out.set(c, offset); offset += c.length; }

  let binary = '';
  for (let i = 0; i < out.length; i++) binary += String.fromCharCode(out[i]);
  return btoa(binary);
}

async function decompress(b64: string): Promise<string> {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  const ds = new DecompressionStream('deflate-raw');
  const writer = ds.writable.getWriter();
  writer.write(bytes);
  writer.close();

  const chunks: Uint8Array[] = [];
  const reader = ds.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) { out.set(c, offset); offset += c.length; }

  return new TextDecoder().decode(out);
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function encodeArmyQR(army: ArmyList): Promise<string> {
  const compact: Compact = {
    v: 1,
    n: army.name,
    f: army.factionId,
    c: army.compositionId,
    m: army.matchedPlayFormats,
    p: army.pointsLimit,
    e: army.entries.map(packEntry),
  };
  if (army.subOrderId) compact.s = army.subOrderId;
  const b64 = await compress(JSON.stringify(compact));
  return PREFIX + b64;
}

/** Returns the decoded ArmyList, or null if the data is not a recognised format. */
export async function decodeArmyQR(data: string): Promise<ArmyList | null> {
  try {
    if (data.startsWith(PREFIX)) {
      const json = await decompress(data.slice(PREFIX.length));
      const compact = JSON.parse(json) as Compact;
      const now = new Date().toISOString();
      return {
        id: crypto.randomUUID(),
        name: compact.n,
        factionId: compact.f,
        compositionId: compact.c,
        subOrderId: compact.s,
        matchedPlayFormats: compact.m as ArmyList['matchedPlayFormats'],
        pointsLimit: compact.p,
        entries: compact.e.map(unpackEntry),
        createdAt: now,
        updatedAt: now,
      };
    }
    // Legacy: raw JSON emitted by older app versions
    const parsed = JSON.parse(data) as ArmyList;
    if (parsed.id && parsed.name && parsed.factionId && Array.isArray(parsed.entries)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}
